/**
 * Appeal status options for the Appeal Summary form.
 *
 * Values are the exact strings backend `pharmacy/appeal/update` accepts for
 * `appeal_status`, so what the dropdown holds can be posted as-is.
 */

import { SelectOption } from "../../../sidePanel/submissionSummarySidePanel/utils/resolveSelectOptionValue";

/** Backend `AppealStatus` values, in workflow order. */
export const APPEAL_STATUS_VALUES = [
  "Not Started",
  "In Progress",
  "Submitted",
  "Done",
  "Appeal Approved",
  "Appeal Denied",
] as const;

/** Dropdown options for the appeal status field. */
export const APPEAL_STATUS_SELECT_OPTIONS: SelectOption[] =
  APPEAL_STATUS_VALUES.map((value) => ({ label: value, value }));

/**
 * Normalizes a stored appeal status to the matching backend value.
 *
 * Accepts both the backend strings (`Not Started`) and the snake_case form the
 * table rows can carry (`not_started`), so a saved row prefills the dropdown.
 *
 * @param rawStatus - `appeal_status` from the appeal row
 * @returns Canonical backend value, or an empty string when unrecognized
 */
export const resolveAppealStatusValue = (
  rawStatus: string | null | undefined,
): string => {
  const trimmed = rawStatus?.trim() ?? "";
  if (!trimmed) {
    return "";
  }

  const normalized = trimmed.toLowerCase().replace(/[\s_]+/g, " ");

  return (
    APPEAL_STATUS_VALUES.find((value) => value.toLowerCase() === normalized) ??
    ""
  );
};

/**
 * Returns the appeal status options, injecting the row's current value when it
 * is not one of the known statuses so the select can still show it.
 *
 * @param currentStatus - Resolved appeal status for the open row
 * @returns Select options for the appeal status field
 */
export const getAppealStatusSelectOptions = (
  currentStatus: string,
): SelectOption[] => {
  const trimmed = currentStatus.trim();
  if (
    !trimmed ||
    APPEAL_STATUS_SELECT_OPTIONS.some((option) => option.value === trimmed)
  ) {
    return APPEAL_STATUS_SELECT_OPTIONS;
  }

  return [{ label: trimmed, value: trimmed }, ...APPEAL_STATUS_SELECT_OPTIONS];
};
